<template>
  <UiToastHost />
  <LoginPage v-if="showLogin" />

  <div
    v-else
    :class="[
      'dashboard-shell',
      sidebarCollapsed
        ? 'lg:grid-cols-[64px_minmax(0,1fr)] xl:grid-cols-[68px_minmax(0,1fr)]'
        : 'lg:grid-cols-[210px_minmax(0,1fr)] xl:grid-cols-[220px_minmax(0,1fr)]',
    ]"
  >
    <Transition name="fade">
      <button
        v-if="mobileOpen"
        class="fixed inset-0 z-30 bg-slate-950/50 lg:hidden"
        aria-label="Đóng menu"
        @click="mobileOpen = false"
      />
    </Transition>
    <aside
      :class="[
        'fixed inset-y-0 left-0 z-40 w-[min(84vw,240px)] transform transition-all duration-200 lg:static lg:translate-x-0',
        sidebarCollapsed ? 'lg:w-[64px] xl:w-[68px]' : 'lg:w-[210px] xl:w-[220px]',
        mobileOpen ? 'translate-x-0' : '-translate-x-full',
      ]"
    >
      <TopNavBar
        :full-name="fullName"
        :team="team"
        :collapsed="sidebarCollapsed && !mobileOpen"
        @toggle-collapse="sidebarCollapsed = !sidebarCollapsed"
        @navigate="mobileOpen = false"
      />
    </aside>

    <main class="dashboard-main">
      <header
        class="sticky top-0 z-20 border-b border-white/10 bg-brand-900/80 backdrop-blur"
      >
        <div
          class="mx-auto flex min-h-[60px] max-w-[1600px] items-center justify-between gap-2 px-3 py-1.5 sm:px-4 lg:px-6"
        >
          <div class="flex min-w-0 items-center gap-2 text-white">
            <UiButton
              variant="ghost"
              size="icon"
              icon="menu"
              class="bg-white/10 text-white hover:bg-white/20 lg:hidden"
              @click="mobileOpen = true"
            />
            <div class="min-w-0 text-left leading-none">
              <p class="truncate text-[11px] font-medium leading-4 text-blue-100">
                Hệ thống quản lý tài sản
              </p>
              <p class="truncate text-base font-black leading-6 sm:text-lg">
                Dashboard vận hành
              </p>
            </div>
          </div>

          <div class="flex shrink-0 items-center gap-1.5 text-white">
            <div
              class="flex min-h-8 shrink-0 items-center gap-1.5 rounded-lg border border-white/15 bg-white/10 px-2 py-1 shadow-sm backdrop-blur"
              title="Thời gian hiện tại"
            >
              <Clock :size="14" :stroke-width="2" class="hidden text-blue-100 sm:block" />
              <div class="text-right leading-none">
                <p class="font-mono text-xs font-bold tabular-nums tracking-wide">
                  {{ currentTimeText }}
                </p>
                <p class="mt-0.5 whitespace-nowrap text-[8px] font-medium text-blue-100 sm:text-[9px]">
                  {{ currentDateText }}
                </p>
              </div>
            </div>

            <div
              class="hidden min-h-9 shrink-0 items-center gap-1.5 rounded-full border border-white/15 bg-white/10 px-2 py-1 md:flex"
            >
              <div
                class="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-white text-[11px] font-bold text-brand-700"
              >
                {{ initials }}
              </div>
              <div class="max-w-[220px] text-left leading-none">
                <p class="truncate text-[11px] font-semibold leading-4">
                  {{ fullName || "Người dùng" }}
                </p>
                <p class="truncate text-[10px] leading-3 text-blue-100">
                  {{ team || "Chưa có khoa/phòng" }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <router-view />
    </main>
  </div>

  <div
    v-if="sessionExpired"
    class="fixed inset-0 z-[100] grid place-items-center bg-slate-950/60 p-4 backdrop-blur-sm"
    role="dialog"
    aria-modal="true"
    aria-labelledby="session-expired-title"
  >
    <div class="w-full max-w-md rounded-3xl border border-slate-200 bg-white p-6 text-center shadow-2xl sm:p-8">
      <div class="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-amber-50 text-amber-600">
        <ShieldAlert :size="30" :stroke-width="2" />
      </div>
      <h2 id="session-expired-title" class="mt-5 text-xl font-black text-slate-900">
        Phiên đăng nhập đã hết hạn
      </h2>
      <p class="mt-2 text-sm leading-6 text-slate-600">
        Tài khoản đã hết hạn đăng nhập. Vui lòng đăng nhập lại để tiếp tục sử dụng hệ thống.
      </p>
      <UiButton class="mt-6" icon="login" size="lg" block @click="goToRelogin">
        Đăng nhập lại
      </UiButton>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { Clock, ShieldAlert } from "lucide-vue-next";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import AuthRepository from "@/services/AuthRepository";
import TopNavBar from "@/components/TopNavBar.vue";
import LoginPage from "@/pages/LoginPage.vue";
import UiToastHost from "@/components/ui/UiToastHost.vue";

export default {
  components: { Clock, ShieldAlert, TopNavBar, LoginPage, UiToastHost },
  setup() {
    const store = useStore();
    const route = useRoute();
    const router = useRouter();
    const mobileOpen = ref(false);
    const sidebarCollapsed = ref(false);
    const currentDateTime = ref(new Date());
    let clockTimer: ReturnType<typeof setInterval> | undefined;
    let sessionTimer: ReturnType<typeof setInterval> | undefined;
    const lastActivityStorageKey = "qlts-last-activity-at";
    const storedLastActivity = Number(window.sessionStorage.getItem(lastActivityStorageKey));
    let lastActivityAt =
      Number.isFinite(storedLastActivity) && storedLastActivity > 0
        ? storedLastActivity
        : Date.now();
    let lastActivityRecordedAt = 0;
    let lastTokenRefreshAt = Date.now();
    let refreshingToken = false;
    const configuredSeconds = (value: unknown, fallback: number) => {
      const seconds = Number(value);
      return Number.isFinite(seconds) && seconds > 0 ? seconds : fallback;
    };
    // BE là nguồn cấu hình duy nhất; FE chỉ quy đổi giá trị BE trả về sang mili giây.
    const idleTimeoutMs = computed(() =>
      configuredSeconds(store.state.idleTimeoutSeconds, 43200) * 1000,
    );
    const refreshIntervalMs = computed(() =>
      configuredSeconds(store.state.refreshIntervalSeconds, 3600) * 1000,
    );
    const activityThrottleMs = 15 * 1000;
    const activityEvents = ["mousemove", "pointerdown", "keydown", "scroll", "touchstart"];
    const fullName = computed(() => store.state.fullName || "");
    const team = computed(() => store.state.team || "");
    const isLoggedIn = computed(() => !!store.state.token);
    const sessionExpired = computed(() => !!store.state.sessionExpired);
    const showLogin = computed(
      () => (!isLoggedIn.value && !sessionExpired.value) || route.name === "login",
    );
    const initials = computed(() => {
      const source = fullName.value || "QL";
      return source
        .split(" ")
        .filter(Boolean)
        .slice(-2)
        .map((part) => part.charAt(0).toUpperCase())
        .join("");
    });
    const currentTimeText = computed(() =>
      new Intl.DateTimeFormat("vi-VN", {
        timeZone: "Asia/Ho_Chi_Minh",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      }).format(currentDateTime.value),
    );
    const currentDateText = computed(() =>
      new Intl.DateTimeFormat("vi-VN", {
        timeZone: "Asia/Ho_Chi_Minh",
        weekday: "short",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      }).format(currentDateTime.value),
    );

    const expireSession = () => {
      store.dispatch("expireSession");
    };

    const saveLastActivity = () => {
      window.sessionStorage.setItem(lastActivityStorageKey, String(lastActivityAt));
    };

    const goToRelogin = () => {
      store.dispatch("clearToken");
      store.dispatch("clearPermission");
      store.dispatch("clearFullName");
      store.dispatch("clearTeam");
      store.dispatch("clearSessionExpired");
      if (route.name !== "login") router.replace({ name: "login" });
    };

    const refreshTokenIfNeeded = async (now = Date.now()) => {
      if (!isLoggedIn.value || sessionExpired.value || refreshingToken || now - lastTokenRefreshAt < refreshIntervalMs.value) return;
      if (now - lastActivityAt >= idleTimeoutMs.value) {
        expireSession();
        return;
      }

      refreshingToken = true;
      try {
        const response = await AuthRepository.refreshSession();
        store.dispatch("setToken", response.data.token);
        store.dispatch("setPermission", response.data.permission);
        store.dispatch("setFullName", response.data.fullname);
        store.dispatch("setSessionConfig", {
          idleTimeoutSeconds: response.data.idleTimeoutSeconds,
          refreshIntervalSeconds: response.data.refreshIntervalSeconds,
        });
        lastTokenRefreshAt = Date.now();
      } catch (error: any) {
        if ([400, 401, 403].includes(error?.response?.status)) expireSession();
      } finally {
        refreshingToken = false;
      }
    };

    const recordActivity = () => {
      if (!isLoggedIn.value || sessionExpired.value) return;
      const now = Date.now();
      if (now - lastActivityRecordedAt < activityThrottleMs) return;
      lastActivityRecordedAt = now;

      if (now - lastActivityAt >= idleTimeoutMs.value) {
        expireSession();
        return;
      }

      lastActivityAt = now;
      saveLastActivity();
      void refreshTokenIfNeeded(now);
    };

    const checkSession = () => {
      if (!isLoggedIn.value || sessionExpired.value) return;
      const now = Date.now();
      if (now - lastActivityAt >= idleTimeoutMs.value) {
        expireSession();
      }
    };

    watch(isLoggedIn, (loggedIn) => {
      if (loggedIn) {
        const now = Date.now();
        lastActivityAt = now;
        lastActivityRecordedAt = now;
        lastTokenRefreshAt = now;
        saveLastActivity();
      }
    });

    onMounted(() => {
      clockTimer = setInterval(() => {
        currentDateTime.value = new Date();
      }, 1000);
      // Kiểm tra thường xuyên để cấu hình idle theo phút có hiệu lực sát thời điểm.
      sessionTimer = setInterval(checkSession, 5 * 1000);
      checkSession();
      activityEvents.forEach((eventName) =>
        window.addEventListener(eventName, recordActivity, { passive: true }),
      );
      window.addEventListener("focus", checkSession);
      document.addEventListener("visibilitychange", checkSession);
    });

    onBeforeUnmount(() => {
      if (clockTimer) clearInterval(clockTimer);
      if (sessionTimer) clearInterval(sessionTimer);
      activityEvents.forEach((eventName) =>
        window.removeEventListener(eventName, recordActivity),
      );
      window.removeEventListener("focus", checkSession);
      document.removeEventListener("visibilitychange", checkSession);
    });

    return {
      showLogin,
      mobileOpen,
      sidebarCollapsed,
      fullName,
      team,
      initials,
      currentTimeText,
      currentDateText,
      sessionExpired,
      goToRelogin,
    };
  },
};
</script>

<style>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
